import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';

export interface NotificationPreferences {
  emailNotifications: boolean;
  securityAlerts: boolean;
}

export function useNotifications() {
  const { user } = useAuth();
  const [preferences, setPreferences] = useState<NotificationPreferences>({
    emailNotifications: true,
    securityAlerts: true
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      loadPreferences();
    }
  }, [user]);

  const loadPreferences = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('notification_preferences')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        setPreferences({
          emailNotifications: data.email_notifications,
          securityAlerts: data.security_alerts
        });
      } else {
        // Create default preferences if none exist
        const { error: insertError } = await supabase
          .from('notification_preferences')
          .insert({
            user_id: user.id,
            email_notifications: true,
            security_alerts: true
          });

        if (insertError) throw insertError;
      }
    } catch (error) {
      console.error('Error loading preferences:', error);
      toast.error('Failed to load notification preferences');
    }
  };

  const savePreferences = async (newPreferences: NotificationPreferences) => {
    if (!user) return false;

    setLoading(true);
    try {
      const { error } = await supabase
        .from('notification_preferences')
        .upsert({
          user_id: user.id,
          email_notifications: newPreferences.emailNotifications,
          security_alerts: newPreferences.securityAlerts
        });

      if (error) throw error;
      setPreferences(newPreferences);
      toast.success('Notification preferences updated');
      return true;
    } catch (error) {
      console.error('Error saving preferences:', error);
      toast.error('Failed to update preferences');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const sendTestEmail = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${supabase.supabaseUrl}/functions/v1/send-notification`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${supabase.supabaseKey}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ test: true })
      });

      if (!response.ok) {
        throw new Error('Failed to send test email');
      }

      const data = await response.json();
      if (data.success) {
        toast.success('Test email sent successfully');
        return true;
      } else {
        throw new Error(data.error || 'Failed to send test email');
      }
    } catch (error) {
      console.error('Error sending test email:', error);
      toast.error('Failed to send test email');
      return false;
    } finally {
      setLoading(false);
    }
  };

  return {
    preferences,
    loading,
    savePreferences,
    sendTestEmail
  };
}